import React, { useState, useMemo } from "react";
import { pairingService } from "../services/pairingService";

// Enhanced V5 Data with aromatic compounds and problem ingredients
const menuItemsData = [
  {
    id: 1,
    name: "Grilled Ribeye Steak",
    profile: {
      fat: 9,
      acidity: 1,
      sweetness: 1,
      bitterness: 4,
      umami: 8,
      spice: 0,
      intensity: 9,
      isOilyFish: false,
      texture: "chewy",
      temp: "hot",
      cookingMethod: "grilling",
      aromaCompounds: ["maillard", "smoke"],
      problemIngredients: [],
    },
  },
  {
    id: 2,
    name: "Steamed Artichoke with Butter",
    profile: {
      fat: 4,
      acidity: 2,
      sweetness: 1,
      bitterness: 6,
      umami: 3,
      spice: 0,
      intensity: 4,
      isOilyFish: false,
      texture: "soft",
      temp: "hot",
      cookingMethod: "steaming",
      aromaCompounds: ["vegetal"],
      problemIngredients: ["cynarin"],
    },
  },
  {
    id: 3,
    name: "Fried Artichoke Hearts",
    profile: {
      fat: 7,
      acidity: 3,
      sweetness: 1,
      bitterness: 4,
      umami: 4,
      spice: 0,
      intensity: 5,
      isOilyFish: false,
      texture: "crispy",
      temp: "hot",
      cookingMethod: "frying",
      aromaCompounds: ["maillard"],
      problemIngredients: ["cynarin"],
    },
  },
  {
    id: 4,
    name: "Spicy Thai Green Curry",
    profile: {
      fat: 7,
      acidity: 4,
      sweetness: 3,
      bitterness: 2,
      umami: 7,
      spice: 7,
      intensity: 8,
      isOilyFish: false,
      texture: "creamy",
      temp: "hot",
      cookingMethod: "stewing",
      aromaCompounds: ["linalool", "gingerol"],
      problemIngredients: [],
    },
  },
  {
    id: 5,
    name: "Sauvignon Blanc & Goat Cheese Salad",
    profile: {
      fat: 5,
      acidity: 8,
      sweetness: 1,
      bitterness: 3,
      umami: 4,
      spice: 0,
      intensity: 6,
      isOilyFish: false,
      texture: "creamy",
      temp: "chilled",
      cookingMethod: "raw",
      aromaCompounds: ["pyrazines", "capric_acid"],
      problemIngredients: [],
    },
  },
];

const winesData = [
  {
    id: 1,
    name: "Young Cabernet Sauvignon",
    category: "red",
    profile: {
      acidity: 7,
      sweetness: 1,
      tannin: 9,
      tanninQuality: 0.6,
      alcohol: 8,
      intensity: 9,
      mouthfeel: "velvety",
      idealTemp: 17,
      currentTemp: 17,
      aromaCompounds: ["blackcurrant", "cedar"],
    },
  },
  {
    id: 2,
    name: "Fine Aged Bordeaux",
    category: "red",
    profile: {
      acidity: 7,
      sweetness: 1,
      tannin: 7,
      tanninQuality: 0.95,
      alcohol: 7,
      intensity: 8,
      mouthfeel: "silky",
      idealTemp: 18,
      currentTemp: 18,
      aromaCompounds: ["smoke", "leather"],
    },
  },
  {
    id: 3,
    name: "Loire Sauvignon Blanc",
    category: "white",
    profile: {
      acidity: 9,
      sweetness: 1,
      tannin: 0,
      tanninQuality: 1.0,
      alcohol: 5,
      intensity: 5,
      mouthfeel: "crisp",
      idealTemp: 8,
      currentTemp: 8,
      aromaCompounds: ["pyrazines", "thiols"],
    },
  },
  {
    id: 4,
    name: "Off-Dry Riesling",
    category: "white",
    profile: {
      acidity: 9,
      sweetness: 5,
      tannin: 0,
      tanninQuality: 1.0,
      alcohol: 4,
      intensity: 6,
      mouthfeel: "light",
      idealTemp: 9,
      currentTemp: 9,
      aromaCompounds: ["linalool", "terpenes"],
    },
  },
  {
    id: 5,
    name: "Fino Sherry",
    category: "fortified",
    profile: {
      acidity: 7,
      sweetness: 1,
      tannin: 1,
      tanninQuality: 1.0,
      alcohol: 8,
      intensity: 6,
      mouthfeel: "crisp",
      idealTemp: 7,
      currentTemp: 7,
      aromaCompounds: ["acetaldehyde", "saline"],
    },
  },
];

const PairingDemo = () => {
  const [selectedFoodIds, setSelectedFoodIds] = useState([1]);
  const [userProfile, setUserProfile] = useState({
    tanninSensitivity: 1.0,
    acidPreference: 1.0,
  });
  // Algorithm version selector (NEW in v5)
  const [algorithmVersion, setAlgorithmVersion] = useState("v5");
  // NEW V4 State
  const [preferenceMode, setPreferenceMode] = useState(false);
  const [ghostWineProfile, setGhostWineProfile] = useState({
    acidity: 7,
    sweetness: 1,
    tannin: 9,
    alcohol: 8,
    intensity: 9,
  });
  const [pairingBias, setPairingBias] = useState(0.5);

  const handleFoodSelection = (id) => {
    setSelectedFoodIds((prev) =>
      prev.includes(id) ? prev.filter((foodId) => foodId !== id) : [...prev, id]
    );
  };

  const selectedFoods = useMemo(
    () => menuItemsData.filter((item) => selectedFoodIds.includes(item.id)),
    [selectedFoodIds]
  );

  // Since useMemo can't handle async directly, we use useEffect and state
  const [wines, setWines] = useState([]);

  // Helper function to calculate v5 algorithm results
  const calculateV5Results = (selectedFoods, userProfile) => {
    return winesData
      .map((wine) => {
        const wineProfile = pairingService.extractWineProfile(wine);
        const foodProfile = pairingService.extractFoodProfile(selectedFoods[0]);

        const analysis = pairingService.getAdvancedPairingScore_v5(
          foodProfile,
          wineProfile,
          userProfile,
          { season: "any", occasion: "any" }
        );

        return {
          wine,
          pairingScore: analysis.score,
          finalScore: analysis.score,
          similarityScore: 0,
          normalizedScore: Math.max(
            0,
            Math.min(100, (analysis.score + 20) * 2.5)
          ),
          explanation: analysis.interpretation,
          confidence: pairingService._getConfidenceLevel(analysis.score),
          pairingType: analysis.score > 5 ? "complementary" : "contrasting",
          detailedAnalysis: analysis.rationale,
          colorClass: analysis.colorClass,
        };
      })
      .sort((a, b) => b.pairingScore - a.pairingScore);
  };

  // Helper function to calculate v4 algorithm results
  const calculateV4Results = (
    selectedFoods,
    userProfile,
    preferenceOptions
  ) => {
    const context = { season: "any", occasion: "any" };
    return pairingService.findWinesForMeal(
      selectedFoods,
      winesData,
      userProfile,
      context,
      preferenceOptions
    );
  };

  // Helper function to build preference options
  const buildPreferenceOptions = React.useCallback(
    () => ({
      enabled: preferenceMode,
      ghostWineProfile: preferenceMode ? ghostWineProfile : null,
      pairingBias: pairingBias,
    }),
    [preferenceMode, ghostWineProfile, pairingBias]
  );

  React.useEffect(() => {
    const calculateRankings = () => {
      if (selectedFoods.length === 0) {
        setWines([]);
        return;
      }

      let results;
      if (algorithmVersion === "v5") {
        results = calculateV5Results(selectedFoods, userProfile);
      } else {
        const preferenceOptions = buildPreferenceOptions();
        results = calculateV4Results(
          selectedFoods,
          userProfile,
          preferenceOptions
        );
      }

      setWines(results.slice(0, 5));
    };

    calculateRankings();
  }, [selectedFoods, userProfile, algorithmVersion, buildPreferenceOptions]);

  return (
    <div className="bg-gray-900 text-white min-h-screen font-sans p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-500">
            SommelAI v5
          </h1>
          <p className="text-lg text-gray-400 mt-2">
            Advanced science-based wine pairing with enhanced tannin quality,
            aromatic bridge logic, and problem ingredient handling
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-3 text-sm">
            <span className="bg-indigo-600 px-3 py-1 rounded-full">
              Tannin Quality Index
            </span>
            <span className="bg-purple-600 px-3 py-1 rounded-full">
              Temperature Modeling
            </span>
            <span className="bg-teal-600 px-3 py-1 rounded-full">
              Aromatic Bridge Logic
            </span>
            <span className="bg-green-600 px-3 py-1 rounded-full">
              Problem Ingredient Solver
            </span>
            <span className="bg-yellow-600 px-3 py-1 rounded-full">
              User Personalization
            </span>
          </div>

          {/* Algorithm Version Toggle */}
          <div className="flex justify-center mt-6">
            <div className="bg-gray-800 p-3 rounded-lg">
              <label
                className="text-sm font-medium text-gray-300 mr-4"
                htmlFor="algorithm-version-select"
              >
                Algorithm Version:
              </label>
              <select
                id="algorithm-version-select"
                value={algorithmVersion}
                onChange={(e) => setAlgorithmVersion(e.target.value)}
                className="bg-gray-700 text-white px-3 py-1 rounded border border-gray-600 focus:ring-2 focus:ring-indigo-500"
              >
                <option value="v4">v4 - Enhanced Personalization</option>
                <option value="v5">v5 - Advanced TQI + Aromatic Bridge</option>
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Column 1: Inputs */}
          <div className="lg:col-span-1 space-y-8">
            {/* Food Selection */}
            <div className="bg-gray-800 p-6 rounded-2xl shadow-lg">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">
                1. Select Your Meal
              </h2>
              <div className="space-y-3">
                {menuItemsData.map((item) => (
                  <label
                    key={item.id}
                    className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedFoodIds.includes(item.id)
                        ? "bg-indigo-500"
                        : "bg-gray-700 hover:bg-gray-600"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedFoodIds.includes(item.id)}
                      onChange={() => handleFoodSelection(item.id)}
                      className="form-checkbox h-5 w-5 text-indigo-600 bg-gray-700 border-gray-600 rounded"
                    />
                    <span className="font-medium">{item.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* User Preferences */}
            <div className="bg-gray-800 p-6 rounded-2xl shadow-lg">
              <h2 className="text-2xl font-semibold mb-4 text-purple-400">
                2. Your Preferences
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">
                    Tannin Sensitivity:{" "}
                    {userProfile.tanninSensitivity.toFixed(1)}x
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="2.0"
                    step="0.1"
                    value={userProfile.tanninSensitivity}
                    onChange={(e) =>
                      setUserProfile((p) => ({
                        ...p,
                        tanninSensitivity: parseFloat(e.target.value),
                      }))
                    }
                    className="w-full h-2 bg-gray-700 rounded-lg cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Less sensitive</span>
                    <span>More sensitive</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">
                    Acid Preference: {userProfile.acidPreference.toFixed(1)}x
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="2.0"
                    step="0.1"
                    value={userProfile.acidPreference}
                    onChange={(e) =>
                      setUserProfile((p) => ({
                        ...p,
                        acidPreference: parseFloat(e.target.value),
                      }))
                    }
                    className="w-full h-2 bg-gray-700 rounded-lg cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Prefer less acid</span>
                    <span>Love high acid</span>
                  </div>
                </div>
              </div>
            </div>

            {/* NEW V4: Preference Pairing Panel */}
            <div className="bg-gray-800 p-6 rounded-2xl shadow-lg">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-semibold text-teal-400">
                  3. Pair by Preference
                </h2>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={preferenceMode}
                    onChange={() => setPreferenceMode(!preferenceMode)}
                    className="form-checkbox h-5 w-5 text-teal-600 bg-gray-700 border-gray-600 rounded"
                  />
                  <span className="ml-2 text-sm">Enable</span>
                </label>
              </div>
              {preferenceMode && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-400">
                    Define a wine you like, and we'll find similar options on
                    our list.
                  </p>
                  <div>
                    <label className="text-sm font-medium">
                      Acidity: {ghostWineProfile.acidity}
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={ghostWineProfile.acidity}
                      onChange={(e) =>
                        setGhostWineProfile((p) => ({
                          ...p,
                          acidity: parseInt(e.target.value),
                        }))
                      }
                      className="w-full h-2 bg-gray-700 rounded-lg cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">
                      Sweetness: {ghostWineProfile.sweetness}
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={ghostWineProfile.sweetness}
                      onChange={(e) =>
                        setGhostWineProfile((p) => ({
                          ...p,
                          sweetness: parseInt(e.target.value),
                        }))
                      }
                      className="w-full h-2 bg-gray-700 rounded-lg cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">
                      Tannin: {ghostWineProfile.tannin}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="10"
                      value={ghostWineProfile.tannin}
                      onChange={(e) =>
                        setGhostWineProfile((p) => ({
                          ...p,
                          tannin: parseInt(e.target.value),
                        }))
                      }
                      className="w-full h-2 bg-gray-700 rounded-lg cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">
                      Alcohol: {ghostWineProfile.alcohol}
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={ghostWineProfile.alcohol}
                      onChange={(e) =>
                        setGhostWineProfile((p) => ({
                          ...p,
                          alcohol: parseInt(e.target.value),
                        }))
                      }
                      className="w-full h-2 bg-gray-700 rounded-lg cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">
                      Intensity: {ghostWineProfile.intensity}
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={ghostWineProfile.intensity}
                      onChange={(e) =>
                        setGhostWineProfile((p) => ({
                          ...p,
                          intensity: parseInt(e.target.value),
                        }))
                      }
                      className="w-full h-2 bg-gray-700 rounded-lg cursor-pointer"
                    />
                  </div>
                  <hr className="border-gray-700" />
                  <div>
                    <label className="text-sm font-medium">
                      Pairing Strategy: {(pairingBias * 100).toFixed(0)}%
                      similarity
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={pairingBias}
                      onChange={(e) =>
                        setPairingBias(parseFloat(e.target.value))
                      }
                      className="w-full h-2 bg-gray-700 rounded-lg cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Focus on Best Pairing</span>
                      <span>Focus on Similarity</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Column 2: Results Section */}
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-gray-800 p-6 rounded-2xl shadow-lg">
              <h2 className="text-2xl font-semibold mb-4 text-yellow-400">
                Wine Recommendations
              </h2>

              {wines.length > 0 ? (
                <div className="space-y-4">
                  {wines.map((result, index) => (
                    <div
                      key={result.wine.id}
                      className="bg-gray-700 p-4 rounded-lg"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-bold text-lg text-white">
                            {index + 1}. {result.wine.name}
                          </h3>
                          <p className="text-sm text-gray-400">
                            {result.wine.category}
                          </p>
                        </div>
                        <div className="text-right">
                          <p
                            className={`text-2xl font-bold ${result.colorClass}`}
                          >
                            {result.finalScore.toFixed(1)}
                          </p>
                          <p className="text-xs text-gray-400">
                            {preferenceMode ? "Final Score" : "Pairing Score"}
                          </p>
                        </div>
                      </div>

                      <p
                        className={`text-sm font-medium mb-2 ${result.colorClass}`}
                      >
                        {result.explanation}
                      </p>

                      {/* V4 Enhanced Display */}
                      {preferenceMode && (
                        <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
                          <div className="bg-gray-800 p-2 rounded">
                            <span className="text-gray-400">Food Pairing:</span>
                            <span className="ml-2 font-bold text-blue-400">
                              {result.pairingScore.toFixed(1)}
                            </span>
                          </div>
                          <div className="bg-gray-800 p-2 rounded">
                            <span className="text-gray-400">Similarity:</span>
                            <span className="ml-2 font-bold text-green-400">
                              {result.similarityScore.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Expandable Details */}
                      <details className="mt-3">
                        <summary className="cursor-pointer text-sm text-indigo-400 hover:text-indigo-300">
                          View detailed analysis
                        </summary>
                        <div className="mt-3 border-t border-gray-600 pt-3 space-y-2 text-xs">
                          {(
                            result.detailedAnalysis ||
                            result.rationale ||
                            []
                          ).map((r, i) => (
                            <p
                              key={`rationale-${result.wine.id}-${i}`}
                              className={
                                (r.points || r.p || "").startsWith("+")
                                  ? "text-green-300"
                                  : "text-red-300"
                              }
                            >
                              <span className="font-bold">
                                {r.points || r.p}:
                              </span>{" "}
                              {r.reason || r.r}
                            </p>
                          ))}
                        </div>
                      </details>
                    </div>
                  ))}
                </div>
              ) : (
                (() => {
                  if (selectedFoods.length === 0) {
                    return (
                      <div className="text-center py-10 text-gray-500">
                        <p>
                          Please select one or more menu items to see wine
                          recommendations.
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div className="text-center py-10 text-gray-500">
                      <p>Calculating recommendations...</p>
                    </div>
                  );
                })()
              )}
            </div>
          </div>
        </div>

        {/* Algorithm Information */}
        <div className="mt-8 bg-gray-800 p-6 rounded-2xl shadow-lg">
          <h3 className="text-xl font-semibold mb-4 text-indigo-400">
            About the Science-Based Algorithm {algorithmVersion.toUpperCase()}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-sm text-gray-300">
            <div>
              <h4 className="font-semibold text-white mb-2">
                Enhanced Food Analysis
              </h4>
              <ul className="space-y-1">
                <li>• Fat/Richness (1-10)</li>
                <li>• Acidity (1-10)</li>
                <li>• Sweetness (1-10)</li>
                <li>• Bitterness (1-10)</li>
                <li>• Umami (1-10)</li>
                <li>• Spice/Heat (0-10)</li>
                <li>• Intensity (1-10)</li>
                <li>
                  •{" "}
                  <span className="text-yellow-400">Texture & Temperature</span>
                </li>
                {algorithmVersion === "v5" && (
                  <>
                    <li>
                      • <span className="text-green-400">Aroma Compounds</span>
                    </li>
                    <li>
                      •{" "}
                      <span className="text-red-400">Problem Ingredients</span>
                    </li>
                  </>
                )}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-2">
                Enhanced Wine Analysis
              </h4>
              <ul className="space-y-1">
                <li>• Acidity (1-10)</li>
                <li>• Sweetness/RS (1-10)</li>
                <li>• Tannin (0-10)</li>
                <li>• Alcohol (1-10)</li>
                <li>• Body/Intensity (1-10)</li>
                <li>
                  •{" "}
                  <span className="text-yellow-400">
                    Tannin Quality (0.1-1.0)
                  </span>
                </li>
                <li>
                  • <span className="text-yellow-400">Mouthfeel & Tags</span>
                </li>
                <li>
                  • <span className="text-yellow-400">Serving Temperature</span>
                </li>
                {algorithmVersion === "v5" && (
                  <li>
                    • <span className="text-green-400">Aroma Compounds</span>
                  </li>
                )}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-2">
                {algorithmVersion === "v5"
                  ? "Advanced Features"
                  : "Personalization"}
              </h4>
              <ul className="space-y-1">
                {algorithmVersion === "v5" ? (
                  <>
                    <li>
                      •{" "}
                      <span className="text-indigo-400">
                        Tannin Quality Index
                      </span>
                    </li>
                    <li>
                      •{" "}
                      <span className="text-purple-400">
                        Dynamic Temperature
                      </span>
                    </li>
                    <li>
                      •{" "}
                      <span className="text-teal-400">
                        Aromatic Bridge Logic
                      </span>
                    </li>
                    <li>
                      •{" "}
                      <span className="text-red-400">
                        Problem Ingredient Solver
                      </span>
                    </li>
                    <li>
                      •{" "}
                      <span className="text-yellow-400">
                        User Personalization
                      </span>
                    </li>
                  </>
                ) : (
                  <>
                    <li>• Tannin Sensitivity</li>
                    <li>• Acid Preference</li>
                    <li>• Quality Modifiers</li>
                    <li>• Temperature Analysis</li>
                    <li>• Preference Matching</li>
                    <li>• Ghost Wine Profiles</li>
                  </>
                )}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-2">
                {algorithmVersion === "v5"
                  ? "Scoring Interactions"
                  : "Preference Matching"}
              </h4>
              <ul className="space-y-1">
                {algorithmVersion === "v5" ? (
                  <>
                    <li>• Base Component Matching</li>
                    <li>• Quality-Adjusted Penalties</li>
                    <li>• Temperature Contrast Analysis</li>
                    <li>• Shared Aroma Bonuses</li>
                    <li>• Cynarin/Artichoke Mitigation</li>
                  </>
                ) : (
                  <>
                    <li>
                      •{" "}
                      <span className="text-teal-400">
                        Ghost Wine Similarity
                      </span>
                    </li>
                    <li>
                      •{" "}
                      <span className="text-teal-400">
                        Pairing Strategy Bias
                      </span>
                    </li>
                    <li>• Multi-dimensional comparison</li>
                    <li>• Balanced scoring system</li>
                    <li>• Best pairing vs. similarity</li>
                    <li>• Personal taste matching</li>
                  </>
                )}
              </ul>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gray-700 rounded-lg">
            <h4 className="font-semibold text-green-400 mb-2">
              New in Version {algorithmVersion === "v5" ? "5" : "4"}:
            </h4>
            <p className="text-sm">
              {algorithmVersion === "v5" ? (
                <>
                  Advanced Tannin Quality Index (TQI) adjusts pairing scores
                  based on tannin refinement. Dynamic temperature modeling
                  considers both wine serving temp and food temperature
                  contrasts. Aromatic bridge logic identifies shared aroma
                  compounds for enhanced harmony. Problem ingredient solver
                  specifically handles cynarin (artichoke) and other challenging
                  ingredients with mitigation strategies.
                </>
              ) : (
                <>
                  Enhanced with user personalization (tannin sensitivity, acid
                  preference), temperature analysis with serving optimization,
                  and preference-based pairing using "ghost wine" similarity
                  matching. Combines scientific food-wine compatibility with
                  personal taste preferences for superior recommendations.
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PairingDemo;
